import { describeOrigin, KIND_LABELS, TASK_KINDS, type Task, type TaskKind } from '@shared/domain.ts';
import type { DataState } from '@/data/store.ts';
import { cn } from '@/lib/format.ts';
import type { Option } from './ui.tsx';

/**
 * Four stable, muted colours — one per kind, the same everywhere a kind is
 * shown, so the board can be read by colour once it has been read by word.
 * `card` is the pale list row, `dark` the detail pane's shell.
 */
const KIND_TONES: Record<TaskKind, { card: string; dark: string }> = {
  bug: { card: 'bg-warn/10 text-warn', dark: 'bg-warnx/14 text-warnx' },
  enhancement: { card: 'bg-[rgba(46,94,160,0.12)] text-[#2e5ea0]', dark: 'bg-[rgba(140,180,240,0.16)] text-[#a9c6f2]' },
  feature: { card: 'bg-violet/12 text-violet', dark: 'bg-violetx/16 text-violetx' },
  chore: { card: 'bg-[rgba(91,97,120,0.14)] text-[#5b6178]', dark: 'bg-pill text-soft' },
};

/** Nothing at all when a task has no kind: absence reads as absence, never as a guess. */
export function KindPill({
  kind,
  variant = 'card',
  className,
}: {
  kind: TaskKind | null;
  variant?: 'card' | 'dark';
  className?: string;
}) {
  if (!kind) return null;
  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-2 py-[2px] text-[11px] font-semibold whitespace-nowrap',
        KIND_TONES[kind][variant],
        className,
      )}
    >
      {KIND_LABELS[kind]}
    </span>
  );
}

/** For a select: every kind, led by "none" under whatever label the caller wants. */
export function kindOptions(none: string): Option[] {
  return [{ value: 'none', label: none }, ...TASK_KINDS.map((kind) => ({ value: kind, label: KIND_LABELS[kind] }))];
}

/**
 * "from Kyrre", "Brigg #412", "schedule: Nightly sweep" — where the task came
 * from, with the names only the board's own cache knows filled in.
 */
export function originText(task: Task, data: Pick<DataState, 'profiles'>): string {
  // A routine's own task names it in `origin_ref` — never the routine it is
  // assigned to now, which may be another one it was handed to.
  const person = task.created_by ? (data.profiles[task.created_by]?.name ?? null) : null;
  return describeOrigin(task, task.origin === 'routine' ? null : person);
}
